import { useProjectParams } from '@hooks/useProjectParams';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { defaultColDef } from 'components/AgGrid/AgGridTable/helpers';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { ITabItem } from 'components/Form';
import { TableTopPanel } from 'components/TableTopPanel';
import { RequestStatus } from 'features/request/requestSlice';
import { type ConstructionTypeData, ContractStatusEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { methodologyMap } from 'types/methodology';
import { Button, cnMixSpace, Layout, Loader, Tabs } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import type { CSSProperties, FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ProjectPageLayout } from '../../Layouts/ProjectPageLayout';
import { useContractConstructionTypes } from '../hooks/useContractConstructionTypes';
import { useUerContractSeasons } from '../hooks/useUerContractSeasons';
import { CreatingTypes } from '../OtherCreate/commonData';
import { CreateOtherModal } from '../OtherCreate/CreateOtherModal';

import { AddOtherBtn } from './components/AddOtherBtn';
import { useContractOtherCosts } from './hooks/useContractOtherCosts';
import { useContractOtherCostsColumns } from './hooks/useContractOtherCostsColumns';
import { useContractOtherCostsGrouped } from './hooks/useContractOtherCostsGrouped';
import { useHasOthers } from './hooks/useHasOthers';
import { useUerRemoveUnusedContractOtherCosts } from './hooks/useUerRemoveUnusedContractOtherCosts';

import Styles from './ContractOthers.module.css';

const xl = cnMixSpace({
    mB: 'xl',
});

const title = 'Лимиты по прочим затратам';

export const ContractOthers: FC = () => {
    const { id: projectId, methodology } = useProjectParams();
    const { projectData } = useProjectData(projectId);
    const { contractId } = projectData || {};

    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);
    const tableTopPanelRef = useRef<HTMLDivElement>(null);

    const [activeTab, setActiveTab] = useState<ITabItem>();
    const [createOpened, setCreateOpened] = useState<
        | {
              type?: CreatingTypes;
          }
        | boolean
    >(false);
    const [position, setPosition] = useState<number | undefined>();

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const isUer = useMemo(() => methodologyMap[ProjectMethodologyEnum.Uer] === methodology, [methodology]);
    const projectMethodology = useMemo(
        () => (isUer ? ProjectMethodologyEnum.Uer : ProjectMethodologyEnum.Bim),
        [isUer],
    );

    const { contract: contractRules } = useAccess();
    const [seasons] = useUerContractSeasons(contractId, isUer);
    const contractHasOthers = useHasOthers(contractId, isUer);

    const [contractConstructionTypes, contractConstructionTypesStatus] = useContractConstructionTypes(
        contractId,
        projectMethodology,
    );

    const [data, contractOtherCostsStatus, , , , , doReload, changeFilterHandler] = useContractOtherCosts({
        contractId,
        constructionTypeId: activeTab?.id as number,
        methodology: projectMethodology,
    });

    const isContractCompletedOrDelayed = useMemo(
        () =>
            [ContractStatusEnum.Completed, ContractStatusEnum.Delayed].includes(
                projectData?.contractNavigation?.data?.contractNavData?.status as ContractStatusEnum,
            ),
        [projectData?.contractNavigation?.data?.contractNavData?.status],
    );

    const { columns, fullSetSettingsColumns, isColumnVisible, tableSettingsConfirm } = useContractOtherCostsColumns({
        isUer,
        seasons,
        costRounding: projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
        constructionTypes: contractConstructionTypes,
        contractId,
        constructionTypeId: activeTab?.id as number,
        onDeleteSuccess: doReload,
        hasWorkOrders: !!projectData?.contractNavigation?.data?.contractNavData?.hasWorkOrders,
        methodology: projectMethodology,
        changeFilterHandler,
    });

    const { rowsGrouped, itemsCount } = useContractOtherCostsGrouped({ items: data?.items });

    const { uerRemoveUnusedContractOtherCostsStatus, uerRemoveUnusedContractOtherCostsMakeRequest } =
        useUerRemoveUnusedContractOtherCosts(contractId, doReload);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: uerRemoveUnusedContractOtherCostsMakeRequest,
        });

    const sortedConstructionTypesTabsItems = useMemo(
        () =>
            contractConstructionTypes
                ?.map((t: ConstructionTypeData) => ({ id: t.id, label: t.name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [contractConstructionTypes],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}/`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const isCalculationCreated = useMemo(
        () =>
            !!projectData?.contractNavigation?.data?.contractNavItems?.find((obj) => obj.name === 'Расчет по договору'),
        [projectData?.contractNavigation?.data?.contractNavItems],
    );

    const hasWorkOrders = useMemo(
        () =>
            projectData?.contractNavigation?.data?.contractNavData?.hasWorkOrders ||
            !!projectData?.contractNavigation?.data?.workOrderNavs?.length,
        [
            projectData?.contractNavigation?.data?.contractNavData?.hasWorkOrders,
            projectData?.contractNavigation?.data?.workOrderNavs?.length,
        ],
    );

    const isCreateDisabled = useMemo(() => {
        if (isContractCompletedOrDelayed || !activeTab?.id || isCalculationCreated || !contractRules.canCreate) {
            return true;
        }

        if (isUer) {
            return (!hasWorkOrders && contractHasOthers) || !seasons.length;
        }

        return !contractConstructionTypes?.length || !!projectData?.contractNavigation?.data?.workOrderNavs?.length;
    }, [
        activeTab?.id,
        contractConstructionTypes?.length,
        contractHasOthers,
        contractRules.canCreate,
        hasWorkOrders,
        isCalculationCreated,
        isContractCompletedOrDelayed,
        isUer,
        projectData?.contractNavigation?.data?.workOrderNavs?.length,
        seasons.length,
    ]);

    const textEmptyData = useMemo(() => {
        if (isUer && !seasons.length) {
            return 'Для добавления прочих необходимо задать сезоны на вкладке "Ценовые параметры"';
        }

        if (!isUer && !contractConstructionTypes?.length) {
            return 'Для добавления прочих необходимо задать виды строительства на вкладке "Основная информация" Договора';
        }

        return 'Для отображения данных создайте прочие затраты';
    }, [contractConstructionTypes?.length, isUer, seasons.length]);

    const onTabChange = useCallback(
        ({ value }) => {
            setActiveTab(value);
        },
        [setActiveTab],
    );

    const onCreateClick = useCallback((creatingType?: CreatingTypes) => {
        if (creatingType) {
            setCreateOpened({
                type: creatingType,
            });
        } else {
            setCreateOpened(true);
        }
    }, []);

    const onCreateClose = useCallback(() => {
        setCreateOpened(false);
    }, []);

    useEffect(() => {
        if (sortedConstructionTypesTabsItems?.length) {
            setActiveTab(sortedConstructionTypesTabsItems[0]);
        }
    }, [sortedConstructionTypesTabsItems]);

    useEffect(() => {
        if (tableTopPanelRef.current) {
            setPosition(tableTopPanelRef.current?.getBoundingClientRect().bottom);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableTopPanelRef.current?.getBoundingClientRect().bottom, isHeaderOpened]);

    const creatingTypeProp = useMemo(() => {
        if (typeof createOpened === 'object' && 'type' in createOpened) {
            return createOpened.type ?? CreatingTypes.MANUAL;
        }

        return CreatingTypes.MANUAL;
    }, [createOpened]);

    const contractOthersContext = {
        tableUniqueId: 'contractOthersContext',
    };

    const isRemoveDisabled = useMemo(() => {
        const canEditRules = isUer ? contractRules?.canEdit : true;

        return (
            !canEditRules || !rowsGrouped.length || uerRemoveUnusedContractOtherCostsStatus === RequestStatus.FETCHING
        );
    }, [contractRules?.canEdit, isUer, rowsGrouped.length, uerRemoveUnusedContractOtherCostsStatus]);

    return (
        <ProjectPageLayout
            title={title}
            breadCrumbsItems={breadCrumbsItems}
            isHeaderCollapsable
            headerBottom={
                <div className="wrapperToolbarLine">
                    <AddOtherBtn
                        style={{ marginRight: '10px', width: 154 }}
                        isDisabled={isCreateDisabled}
                        onCreateClick={onCreateClick}
                        methodology={projectMethodology}
                        hasWorkOrders={hasWorkOrders}
                    />
                    {isUer && (
                        <Button
                            style={{ marginRight: '10px' }}
                            label="Удалить неиспользуемые записи"
                            view="secondary"
                            size="s"
                            disabled={isRemoveDisabled}
                            onClick={handleOpenConfirmationDialog}
                        />
                    )}
                </div>
            }
        >
            <Layout direction="column">
                <>
                    <Layout className={xl}>
                        <ConfirmationDialog
                            textHeader="Удаление неиспользуемых записей"
                            textConfirmation="Вы уверены, что хотите удалить записи Прейскуранта в статусе «Новая запись», неиспользуемые в Наряд-заказах, Актах КС-2 и Изм. к НЗ?"
                            cancelButtonText="Не удалять"
                            confirmButtonText="Удалить"
                            isOpened={isOpenConfirmationDialog}
                            onSubmit={handleActionQuery}
                            onClose={handleCloseConfirmationDialog}
                        />

                        {createOpened && activeTab?.id && (
                            <CreateOtherModal
                                handleClose={onCreateClose}
                                creatingType={creatingTypeProp}
                                currentConstructionTypeId={activeTab?.id}
                                reloadHandler={doReload}
                                constructionTypes={contractConstructionTypes}
                                isCondition
                                noLimitSpan={isUer && !hasWorkOrders}
                                hasWorkOrders={hasWorkOrders}
                                methodology={projectMethodology}
                                contractId={contractId}
                            />
                        )}
                    </Layout>
                    <Layout className={xl}>
                        <Tabs
                            value={activeTab}
                            onChange={onTabChange}
                            items={sortedConstructionTypesTabsItems || []}
                            getItemLabel={(item: { label: string | number }) => item.label}
                            view="bordered"
                        />
                    </Layout>
                    {contractConstructionTypesStatus === RequestStatus.FETCHING ? (
                        <Loader />
                    ) : (
                        <>
                            <TableTopPanel
                                totalItems={itemsCount}
                                fetchedItems={itemsCount}
                                optionalColumns={fullSetSettingsColumns}
                                isColumnVisible={isColumnVisible}
                                handleTableSettingsConfirm={tableSettingsConfirm}
                                showPinColumn
                                tableApi={tableApi}
                                ref={tableTopPanelRef}
                            />
                            <div
                                className={Styles.contractOthersTable}
                                style={
                                    {
                                        '--tableStartPosition': `${position ?? 0}px`,
                                    } as CSSProperties
                                }
                            >
                                <AgGridTable
                                    loading={contractOtherCostsStatus === RequestStatus.FETCHING}
                                    rowData={rowsGrouped}
                                    columnDefs={columns}
                                    animateRows
                                    overlayNoRowsTemplate={textEmptyData}
                                    defaultColDef={defaultColDef}
                                    ref={tableRef}
                                    context={contractOthersContext}
                                />
                            </div>
                        </>
                    )}
                </>
            </Layout>
        </ProjectPageLayout>
    );
};

ContractOthers.displayName = 'ContractOthers';
